import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { FiX } from "react-icons/fi";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./OriDrawer.module.scss";

// Keep in sync with the panel transition duration in OriDrawer.module.scss.
const PANEL_TRANSITION_MS = 240;

interface OriDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  // Scrim label, for when "Close" alone is ambiguous to a screen reader
  // (e.g. "Close chat history"). Defaults to the generic close label.
  closeA11yLabel?: string;
  testId?: string;
  closeTestId?: string;
  children: React.ReactNode;
}

// The shared right-hand slide-over shell used by both the chat-history and QA
// drawers, so the two are visually and behaviourally identical: scrim + slide/
// fade animation, Escape-to-close, focus-on-open, and a Tab focus trap. Callers
// supply only the title and body content.
const OriDrawer: React.FC<OriDrawerProps> = ({
  isOpen,
  onClose,
  title,
  closeA11yLabel,
  testId,
  closeTestId,
  children,
}) => {
  const { t } = useTranslation(["common"]);
  const panelRef = useRef<HTMLDivElement>(null);
  // Stay mounted through the close transition (`isMounted`) while `isEntered`
  // drives the slide/fade. Unmount on a timer rather than transitionend, which
  // never fires under prefers-reduced-motion.
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isEntered, setIsEntered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      // Double rAF: let the browser paint the off-screen start state for one
      // frame before flipping to entered, or the slide-in is skipped.
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setIsEntered(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setIsEntered(false);
    const timer = setTimeout(() => setIsMounted(false), PANEL_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
  }, [isOpen]);

  // Keep Tab within the panel so focus can't reach the live chat behind the scrim.
  const trapFocus = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  if (!isMounted) return null;

  return (
    <div className={clsx(styles.overlay, isEntered && styles.overlayOpen)}>
      <button
        type="button"
        className={styles.scrim}
        aria-label={closeA11yLabel ?? t("common:action.close")}
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={trapFocus}
        data-testid={testId}
      >
        <header className={styles.header}>
          <h2 className={styles.heading}>{title}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t("common:action.close")}
            onClick={onClose}
            data-testid={closeTestId}
          >
            <FiX aria-hidden />
          </button>
        </header>

        <div className={styles.body}>{children}</div>
      </aside>
    </div>
  );
};

export default OriDrawer;
